import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  getSortedRowModel,
} from '@tanstack/react-table'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/features/shadcn/ui/table'
import { useDispatch, useSelector } from 'react-redux'
import { selectSelectedRows, setSelectedRows } from '@/applications/Traffic/trafficReportSlice'
import DataTablePagination from './DataTablePagination'
import DataTableDynamicPagination from './DataTableDynamicPagination'

export default function DataTable({
  columns,
  data,
  defaultSorting = [],
  showSelectedRowsLabel = true,
  showPagination = false,
  showDynamicPagination = false,
  thClassName = '',
  customThead,
}: any) {
  const dispatch = useDispatch()

  // Handle row selection
  const rowSelection = useSelector(selectSelectedRows)
  useEffect(() => {
    if (!data || _.isEmpty(data)) return
    // Select all rows by default. Bail when the existing selection already
    // matches — otherwise a new `data` array reference (even with identical
    // content) would re-dispatch and cause continuous re-renders.
    if (
      rowSelection &&
      Object.keys(rowSelection).length === data.length &&
      data.every((_row: any, i: number) => rowSelection[i] === true)
    ) {
      return
    }
    const _selectedRows = data
      .map((row: any, i: number) => i)
      .reduce((acc: any, i: number) => ({ ...acc, [i]: true }), {})
    dispatch(setSelectedRows(_selectedRows))
  }, [data])

  // Handle sorting
  const [sorting, setSorting] = useState(defaultSorting)

  // Handle pagination
  const [pagination, setPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: 50, //default page size
  })

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onRowSelectionChange: (selectedRows: any) => {
      dispatch(setSelectedRows(selectedRows(rowSelection)))
    },
    onSortingChange: setSorting as any,
    getSortedRowModel: getSortedRowModel(),
    state: {
      pagination,
      rowSelection,
      sorting,
    },
  })

  // Dashboard-style rendering when an editable thead is provided. Wraps the
  // table in `.list-table` so shared dashboard CSS (drag handle, X button,
  // sortable header) applies, and uses plain HTML elements with Bootstrap
  // classes to match the rest of the dashboard.
  if (customThead) {
    const rows = table.getRowModel().rows
    return (
      <div className="list-table">
        <div className="scrollable-table-container table-responsive">
          <table className="table table-striped table-hover table-bordered">
            {customThead}
            <tbody>
              {rows?.length ? (
                rows.map((row: any) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell: any) => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="tw-h-24 tw-text-center">
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showPagination && data && !_.isEmpty(data) && (
          <div className="tw-my-4">
            <DataTablePagination table={table} />
          </div>
        )}

        {showDynamicPagination && data && !_.isEmpty(data) && (
          <div className="tw-my-4">
            <DataTableDynamicPagination table={table} showSelectedRowsLabel={showSelectedRowsLabel} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="tw-border-b">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup: any) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header: any) => {
                return (
                  <TableHead key={header.id} className={`tw-min-w-24 tw-truncate tw-text-right ${thClassName}`}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row: any) => (
              <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map((cell: any) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="tw-h-24 tw-text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {showPagination && data && !_.isEmpty(data) && (
        <div className="tw-my-4">
          <DataTablePagination table={table} />
        </div>
      )}

      {showDynamicPagination && data && !_.isEmpty(data) && (
        <div className="tw-my-4">
          <DataTableDynamicPagination table={table} showSelectedRowsLabel={showSelectedRowsLabel} />
        </div>
      )}
    </div>
  )
}
